import { OnLegacyAuthenticationReadyEvent } from "@Modules/event_module";
import { Blueconic } from "@Modules/blueconic_module";
import { OptionsModule } from "@Modules/options_module";
import { CookieManager } from "@Shared/storage/cookie_manager";
import { LoggerService } from "@Shared/logger";
import { Auth0Client, IdToken } from "@auth0/auth0-spa-js";
import { APP_EVENTS, MNGAuthentication, STORAGE } from "@Core";
import { MNGSession } from "@Modules/index";

const SS_PAYWALL_COOKIE_NAME = "vip-go-seg";
const SS_PAYWALL_COOKIE_VALUE_PREFIX = "vc-v1__has_access";

// Called before the entitlement pipeline has had a chance to run (e.g. ad code
// querying isUserAdFree() immediately on page load), so it can't rely on
// EntitlementService's async code
function readLocalAdFreeStatus(): boolean {
  const session = AuthenticationUtils.getUserDataFromStorage();
  if (!session) return false;
  const hasIdentity = session.idToken || window.MNGAuthentication?.authenticationReady?.idToken;
  return hasIdentity && session.adFree === true;
}

const EmptyMNGAuthentication: MNGAuthentication = {
  authenticationReady: undefined,
  entitlementsReady: undefined,
  isUserAdFree: () => readLocalAdFreeStatus(),
  isUserSubscriber: () => false,
  logoutCallbacks: [],
  postAuthEvents: [],
  preAuthGTMEvents: [],
  customLogout: () => Promise.resolve()
};

export class AuthenticationUtils {
    private static Logger = LoggerService.createLogger("AuthenticationUtils");

    static removeSSPaywallCookie(): void {
        const value = CookieManager.getCookie(SS_PAYWALL_COOKIE_NAME);
        if (typeof value === "string" && value.startsWith(SS_PAYWALL_COOKIE_VALUE_PREFIX)) {
            this.Logger.trace("SS paywall cookie found, removing it");
            CookieManager.deleteAllCookiesByName(SS_PAYWALL_COOKIE_NAME);
        }
    }

    // Reads whatever MNGSession was cached in localStorage on a previous page
    // load. Useful for anything that needs session data before/without going
    // through EntitlementService's async checkEntitlements() flow.
    static getUserDataFromStorage(): MNGSession | null {
        try {
            const raw = window.localStorage.getItem(STORAGE.sessionLocalStorageKey);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    static initializeMNGAuthenticationGlobal(clearExistingValues = false) {
        const existingValues = clearExistingValues ? {} : window.MNGAuthentication || {}
        window.MNGAuthentication = {
            ...EmptyMNGAuthentication,
            ...existingValues
        } 
    }

    static async fireLegacyAuthenticationReadyEvent(auth0Client: Auth0Client): Promise<void> {
        const details: OnLegacyAuthenticationReadyEvent = {
            isAuthenticated: false,
            idToken: false,
            accessToken: false,
            claims: null
        }

        try {
            details.isAuthenticated = await auth0Client.isAuthenticated() || false;
            if (details.isAuthenticated) {
                details.claims = await auth0Client.getIdTokenClaims() || null;
                details.idToken = details.claims?.__raw || false;
            } else {
                details.accessToken = await auth0Client.getTokenSilently() || false;
            }

            const blueconicSettings = OptionsModule.Options?.blueconic;
            if (
                !details.isAuthenticated &&
                !details.accessToken &&
                blueconicSettings?.enabled &&
                blueconicSettings?.bcLowaSegments
            ) {
                AuthenticationUtils.Logger.trace("BlueConic LOWA | entering bcLowaCheck");
                const lowaResults = await Blueconic.bcLowaCheck( true );
                AuthenticationUtils.Logger.trace("BlueConic LOWA | bcLowaCheck result:", lowaResults);
                if ( lowaResults.isSub && lowaResults.uuid ) {
                    AuthenticationUtils.Logger.trace("BlueConic LOWA | authenticated as LOWA");
                    details.isAuthenticated = true;
                    details.claims = { sub: lowaResults.uuid } as unknown as IdToken;
                } else {
                    AuthenticationUtils.Logger.trace("BlueConic LOWA | NOT a subscriber");
                }
            }

            AuthenticationUtils.Logger.trace("Firing legacy authenticationReady event", details);

            window.dispatchEvent(new CustomEvent(APP_EVENTS.onLegacyAuthenticationReady, { detail: details }));
        } catch (error) {

            // fire authenticationReady event with default values if there is an error
            AuthenticationUtils.Logger.trace("Firing legacy authenticationReady event with default values:", details);

            window.dispatchEvent(new CustomEvent(APP_EVENTS.onLegacyAuthenticationReady, { detail: details }));
        }
    }

    /**
     * Triggers Connext logout and waits briefly so its redirect can start
     * before Auth0 logout runs (both involve redirects; they cannot run in parallel).
     */
    static callConnextLogoutWithTimeout(timeoutMs = 300): Promise<void> {
        return new Promise((resolve) => {
            AuthenticationUtils.Logger.trace('About to call Connext Logout...');
            window.Connext?.Logout();
            setTimeout(() => resolve(), timeoutMs);
        });
    }
}
